import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { useBranding, PRESETS, hslStringToHex, hexToHslString } from '@/lib/branding';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Eye, EyeOff, Sparkles, Save, TestTube2, Globe, Coins, Settings as SettingsIcon, FileText, Palette, Zap, Printer } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

export default function Settings() {
  const { t, lang, setLang } = useI18n();
  const { branding, reload, applyLocal } = useBranding();
  const [s, setS] = useState(null);
  const [openaiKey, setOpenaiKey] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [showOpenai, setShowOpenai] = useState(false);
  const [showAnthropic, setShowAnthropic] = useState(false);
  const [testing, setTesting] = useState({ openai: false, anthropic: false, emergent: false });

  const load = useCallback(async () => { const r = await api.get('/settings'); setS(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  if (!s) return null;

  const saveSection = async (patch) => {
    const r = await api.put('/settings', patch);
    setS(r.data);
    toast.success(t('settings_saved'));
    // Refresh branding context if branding fields changed
    if (Object.keys(patch).some(k => k.startsWith('color_') || k === 'logo_url' || k === 'theme_preset' || k === 'nombre')) {
      reload();
    }
  };

  const testKey = async (provider) => {
    setTesting({ ...testing, [provider]: true });
    try {
      let payload;
      if (provider === 'emergent') {
        const r = await api.post('/settings/ai/test-emergent');
        if (r.data.ok) toast.success(`${t('ai_test_ok')}: ${r.data.reply || ''}`);
        else toast.error(`${t('ai_test_fail')}: ${(r.data.error || '').slice(0, 200)}`);
        return;
      }
      const key = provider === 'openai' ? openaiKey : anthropicKey;
      const model = provider === 'openai' ? s.openai_model : s.anthropic_model;
      const r = await api.post('/settings/ai/test', { provider, api_key: key, model });
      if (r.data.ok) toast.success(`${t('ai_test_ok')}: ${r.data.reply || ''}`);
      else toast.error(`${t('ai_test_fail')}: ${(r.data.error || '').slice(0, 200)}`);
    } catch (e) {
      toast.error(t('ai_test_fail'));
    } finally {
      setTesting({ ...testing, [provider]: false });
    }
  };

  const applyPreset = (preset) => {
    const p = PRESETS[preset];
    if (!p) return;
    const patch = {
      theme_preset: preset,
      color_primary: p.color_primary,
      color_secondary: p.color_secondary,
      color_accent: p.color_accent,
      color_background: p.color_background,
    };
    applyLocal(patch);
    saveSection(patch);
  };

  return (
    <div className="space-y-4" data-testid="settings-page">
      <h1 className="text-2xl font-heading font-bold">{t('nav_settings')}</h1>
      <Tabs defaultValue="restaurant">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="restaurant" data-testid="settings-tab-restaurant"><SettingsIcon className="h-3.5 w-3.5 mr-1" /> {t('settings_restaurant')}</TabsTrigger>
          <TabsTrigger value="branding" data-testid="settings-tab-branding"><Palette className="h-3.5 w-3.5 mr-1" /> {lang === 'es' ? 'Marca y colores' : 'Marca e cores'}</TabsTrigger>
          <TabsTrigger value="taxes" data-testid="settings-tab-taxes"><Coins className="h-3.5 w-3.5 mr-1" /> {t('settings_taxes')}</TabsTrigger>
          <TabsTrigger value="invoicing" data-testid="settings-tab-invoicing"><FileText className="h-3.5 w-3.5 mr-1" /> {t('settings_invoicing')}</TabsTrigger>
          <TabsTrigger value="kitchen" data-testid="settings-tab-kitchen"><Printer className="h-3.5 w-3.5 mr-1" /> {lang === 'es' ? 'Cocina/Impresora' : 'Cozinha/Impressora'}</TabsTrigger>
          <TabsTrigger value="language" data-testid="settings-tab-language"><Globe className="h-3.5 w-3.5 mr-1" /> {t('settings_language')}</TabsTrigger>
          <TabsTrigger value="ai" data-testid="settings-tab-ai"><Sparkles className="h-3.5 w-3.5 mr-1" /> {t('settings_ai')}</TabsTrigger>
        </TabsList>

        <TabsContent value="restaurant">
          <Card><CardContent className="p-4 space-y-3 max-w-xl">
            <div><Label>{t('restaurant_name')}</Label><Input defaultValue={s.nombre} onBlur={(e) => saveSection({ nombre: e.target.value })} data-testid="settings-restaurant-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t('nif')}</Label><Input defaultValue={s.nif || ''} onBlur={(e) => saveSection({ nif: e.target.value })} data-testid="settings-nif-input" /></div>
              <div><Label>{t('phone')}</Label><Input defaultValue={s.telefono || ''} onBlur={(e) => saveSection({ telefono: e.target.value })} /></div>
            </div>
            <div><Label>{t('email')}</Label><Input defaultValue={s.email || ''} onBlur={(e) => saveSection({ email: e.target.value })} /></div>
            <div><Label>{t('address')}</Label><Input defaultValue={s.direccion || ''} onBlur={(e) => saveSection({ direccion: e.target.value })} /></div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="branding">
          <Card><CardContent className="p-4 space-y-4 max-w-2xl">
            <div>
              <Label>{lang === 'es' ? 'Tema preestablecido' : 'Tema predefinido'}</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                {Object.entries(PRESETS).map(([key, p]) => (
                  <button
                    key={key}
                    onClick={() => applyPreset(key)}
                    className={`rounded-xl border-2 p-3 text-left transition-all ${s.theme_preset === key ? 'border-[hsl(var(--primary))] shadow-md' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/50'}`}
                    data-testid={`settings-preset-${key}`}
                  >
                    <div className="flex gap-1 mb-2">
                      {[p.color_primary, p.color_secondary, p.color_accent].map((c, i) => (
                        <span key={`preset-${key}-${i}-${c}`} className="h-6 w-6 rounded-full border" style={{ background: `hsl(${c})` }} />
                      ))}
                    </div>
                    <div className="text-xs font-semibold">{p.label}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>{lang === 'es' ? 'Logo URL (imagen del restaurante)' : 'Logo URL (imagem do restaurante)'}</Label>
              <Input
                defaultValue={s.logo_url || ''}
                onBlur={(e) => saveSection({ logo_url: e.target.value })}
                placeholder="https://..."
                data-testid="settings-logo-url-input"
              />
              {s.logo_url && (
                <div className="mt-2 flex items-center gap-2">
                  <img src={s.logo_url} alt="logo" className="h-12 w-12 rounded-lg object-cover border" />
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">{lang === 'es' ? 'Vista previa' : 'Pré-visualização'}</span>
                </div>
              )}
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                {lang === 'es'
                  ? 'Sube tu logo a un servicio (Imgur, Cloudinary, tu web) y pega la URL aquí.'
                  : 'Faça upload do seu logo (Imgur, Cloudinary, seu site) e cole a URL aqui.'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <ColorField label={lang === 'es' ? 'Color primario' : 'Cor primária'} value={s.color_primary} onChange={(v) => { applyLocal({ color_primary: v }); saveSection({ color_primary: v }); }} testId="settings-color-primary" />
              <ColorField label={lang === 'es' ? 'Color secundario' : 'Cor secundária'} value={s.color_secondary} onChange={(v) => { applyLocal({ color_secondary: v }); saveSection({ color_secondary: v }); }} testId="settings-color-secondary" />
              <ColorField label={lang === 'es' ? 'Color de acento' : 'Cor de destaque'} value={s.color_accent} onChange={(v) => { applyLocal({ color_accent: v }); saveSection({ color_accent: v }); }} testId="settings-color-accent" />
              <ColorField label={lang === 'es' ? 'Fondo' : 'Fundo'} value={s.color_background} onChange={(v) => { applyLocal({ color_background: v }); saveSection({ color_background: v }); }} testId="settings-color-background" />
            </div>

            <div className="rounded-xl p-4 border" style={{ background: 'hsl(var(--background))' }}>
              <div className="text-xs uppercase font-semibold text-[hsl(var(--muted-foreground))] mb-2">{lang === 'es' ? 'Vista previa en vivo' : 'Pré-visualização ao vivo'}</div>
              <div className="flex flex-wrap gap-2 items-center">
                <Button size="sm">Botão Primário</Button>
                <Button size="sm" variant="secondary">Secundário</Button>
                <Button size="sm" variant="outline">Outline</Button>
                <span className="px-3 py-1 rounded-md text-sm font-medium bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]">Accent</span>
                <span className="px-3 py-1 rounded-md text-sm font-medium bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]">Muted</span>
              </div>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="taxes">
          <Card><CardContent className="p-4 space-y-3 max-w-xl">
            <div><Label>{t('iva_default')}</Label>
              <Select defaultValue={String(s.iva_default)} onValueChange={(v) => saveSection({ iva_default: Number(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="4">4%</SelectItem><SelectItem value="10">10%</SelectItem><SelectItem value="21">21%</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>{t('food_cost_default')}</Label><Input type="number" defaultValue={s.food_cost_objetivo_pct} onBlur={(e) => saveSection({ food_cost_objetivo_pct: Number(e.target.value) })} /></div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              {lang === 'es' ? 'Tipos IVA España: 4% reducido, 10% hostelería, 21% general/alcohol.' : 'Tipos IVA Espanha: 4% reduzido, 10% hotelaria, 21% geral/álcool.'}
            </p>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="invoicing">
          <Card><CardContent className="p-4 space-y-3 max-w-xl">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t('invoice_serie')}</Label><Input defaultValue={s.serie_actual || ''} onBlur={(e) => saveSection({ serie_actual: e.target.value })} /></div>
              <div><Label>{t('invoice_prefix')}</Label><Input defaultValue={s.invoice_prefix || ''} onBlur={(e) => saveSection({ invoice_prefix: e.target.value })} /></div>
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              {lang === 'es' ? `Próxima factura: ${s.invoice_prefix ? s.invoice_prefix + '-' : ''}${s.serie_actual ? s.serie_actual + '-' : ''}${new Date().getFullYear()}/${String(s.next_invoice_number).padStart(5, '0')}` : `Próxima fatura: ${s.invoice_prefix || ''}${s.serie_actual || ''}/${new Date().getFullYear()}/${String(s.next_invoice_number).padStart(5, '0')}`}
            </p>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="kitchen">
          <Card><CardContent className="p-4 space-y-4 max-w-xl">
            <div>
              <h3 className="font-heading font-semibold mb-1 flex items-center gap-2">
                <Printer className="h-4 w-4 text-[hsl(var(--primary))]" />
                {lang === 'es' ? 'Impresión automática a cocina' : 'Impressão automática para cozinha'}
              </h3>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                {lang === 'es'
                  ? 'Cuando esté activado, al pulsar "Enviar a cocina" en POS se imprimirá automáticamente la comanda en la impresora predeterminada del sistema.'
                  : 'Quando ativo, ao clicar "Enviar à cozinha" no POS imprimirá automaticamente a comanda na impressora padrão do sistema.'}
              </p>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg border bg-[hsl(var(--muted))]/30">
              <div>
                <Label className="text-sm font-semibold">
                  {lang === 'es' ? 'Auto-imprimir al enviar a cocina' : 'Auto-imprimir ao enviar à cozinha'}
                </Label>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                  {lang === 'es' ? 'Activa esta opción si tienes una impresora térmica conectada' : 'Ative se tem uma impressora térmica conectada'}
                </p>
              </div>
              <Switch
                checked={!!s.auto_print_kitchen}
                onCheckedChange={(v) => { saveSection({ auto_print_kitchen: v }); reload(); }}
                data-testid="settings-auto-print-kitchen-switch"
              />
            </div>

            <div>
              <Label>{lang === 'es' ? 'Tipo de impresora de cocina' : 'Tipo de impressora da cozinha'}</Label>
              <Select
                value={s.kitchen_printer_size || '80mm'}
                onValueChange={(v) => { saveSection({ kitchen_printer_size: v }); reload(); }}
              >
                <SelectTrigger data-testid="settings-kitchen-printer-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="80mm">{lang === 'es' ? '80mm — Térmica POS (rollo)' : '80mm — Térmica POS (bobina)'}</SelectItem>
                  <SelectItem value="a4">{lang === 'es' ? 'A4 — Impresora estándar' : 'A4 — Impressora padrão'}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                {lang === 'es'
                  ? 'Tickets 80mm para impresoras térmicas (Epson TM-T20, Star TSP100, Bematech). A4 para impresoras de oficina.'
                  : 'Tickets 80mm para impressoras térmicas (Epson TM-T20, Star TSP100, Bematech). A4 para impressoras de escritório.'}
              </p>
            </div>

            <div className="rounded-xl border bg-[hsl(var(--accent))]/30 p-3 space-y-2">
              <h4 className="text-sm font-semibold flex items-center gap-1.5">
                💡 {lang === 'es' ? 'Cómo conectar tu impresora' : 'Como conectar sua impressora'}
              </h4>
              <ol className="text-xs space-y-1 list-decimal list-inside text-[hsl(var(--muted-foreground))]">
                <li>{lang === 'es' ? 'Conecta la impresora térmica al ordenador (USB) o a la red WiFi/Ethernet' : 'Conecte a impressora térmica ao computador (USB) ou rede WiFi/Ethernet'}</li>
                <li>{lang === 'es' ? 'Instala el driver del fabricante (Windows: el instalador; Linux: CUPS; macOS: System Preferences)' : 'Instale o driver do fabricante (Windows: instalador; Linux: CUPS; macOS: Preferências do Sistema)'}</li>
                <li>{lang === 'es' ? 'Configúrala como "Impresora predeterminada" en tu sistema' : 'Configure como "Impressora padrão" no seu sistema'}</li>
                <li>{lang === 'es' ? 'En tu navegador, en el diálogo de impresión, activa "Imprimir sin diálogo" o configúralo para imprimir automáticamente sin preguntar' : 'No navegador, na caixa de impressão, ative "Imprimir sem diálogo" ou configure para imprimir automaticamente'}</li>
              </ol>
              <p className="text-xs italic text-[hsl(var(--muted-foreground))] mt-1">
                {lang === 'es'
                  ? '⚠️ El navegador puede mostrar el diálogo "Imprimir" la primera vez. Marca "No volver a preguntar" para impresión silenciosa.'
                  : '⚠️ O navegador pode mostrar o diálogo "Imprimir" da primeira vez. Marque "Não perguntar mais" para impressão silenciosa.'}
              </p>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="language">
          <Card><CardContent className="p-4 space-y-3 max-w-md">
            <Label>{t('settings_language')}</Label>
            <Select value={lang} onValueChange={(v) => { setLang(v); saveSection({ idioma: v }); }}>
              <SelectTrigger data-testid="settings-language-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="es">🇪🇸 Español</SelectItem>
                <SelectItem value="pt">🇧🇷 Português</SelectItem>
              </SelectContent>
            </Select>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="ai">
          <Card><CardContent className="p-4 space-y-4 max-w-xl">
            <div>
              <Label>{t('ai_provider')}</Label>
              <Select value={s.ai_provider} onValueChange={(v) => saveSection({ ai_provider: v })}>
                <SelectTrigger data-testid="settings-ai-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="emergent">⚡ Emergent Universal Key (recomendado)</SelectItem>
                  <SelectItem value="openai">OpenAI (própria chave)</SelectItem>
                  <SelectItem value="anthropic">Anthropic Claude (própria chave)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                {lang === 'es'
                  ? 'Emergent Universal Key te da acceso inmediato a OpenAI/Claude/Gemini sin gestionar facturación.'
                  : 'Emergent Universal Key te dá acesso imediato a OpenAI/Claude/Gemini sem gerir faturação.'}
              </p>
            </div>

            {/* EMERGENT */}
            <div className="border rounded-xl p-3 space-y-3 bg-[hsl(var(--accent))]/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-[hsl(var(--accent))]" />
                  <h3 className="font-heading font-semibold text-sm">Emergent Universal Key</h3>
                </div>
                <span className="text-xs">{s.emergent_key_set ? '✓ Activo' : '✗ Não configurado'}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Provider</Label>
                  <Select value={s.emergent_provider || 'openai'} onValueChange={(v) => saveSection({ emergent_provider: v })}>
                    <SelectTrigger data-testid="settings-emergent-provider"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openai">OpenAI</SelectItem>
                      <SelectItem value="anthropic">Anthropic</SelectItem>
                      <SelectItem value="gemini">Gemini</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">{t('ai_default_model')}</Label>
                  <Select value={s.emergent_model || 'gpt-4o-mini'} onValueChange={(v) => saveSection({ emergent_model: v })}>
                    <SelectTrigger data-testid="settings-emergent-model"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(s.emergent_provider === 'anthropic' ? ['claude-4-sonnet-20250514', 'claude-sonnet-4-5-20250929', 'claude-haiku-4-5-20251001'] :
                        s.emergent_provider === 'gemini' ? ['gemini-2.5-flash', 'gemini-2.5-pro', 'gemini-2.5-flash-lite'] :
                        ['gpt-4o-mini', 'gpt-5-mini', 'gpt-5', 'gpt-4.1-mini', 'gpt-4o']).map(m =>
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => testKey('emergent')} disabled={testing.emergent || !s.emergent_key_set} data-testid="settings-emergent-test-button">
                <TestTube2 className="h-3.5 w-3.5 mr-1" /> {testing.emergent ? '...' : t('ai_test')}
              </Button>
            </div>

            {/* OPENAI */}
            <div className="border rounded-xl p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-semibold text-sm">OpenAI</h3>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    {s.openai_api_key_set ? `✓ Configurado (${s.openai_api_key_masked})` : (lang === 'es' ? 'No configurado' : 'Não configurado')}
                  </p>
                </div>
              </div>
              <div><Label className="text-xs">{t('ai_default_model')}</Label>
                <Select value={s.openai_model} onValueChange={(v) => saveSection({ openai_model: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gpt-4o-mini">gpt-4o-mini</SelectItem>
                    <SelectItem value="gpt-4o">gpt-4o</SelectItem>
                    <SelectItem value="gpt-4.1-mini">gpt-4.1-mini</SelectItem>
                    <SelectItem value="gpt-4.1">gpt-4.1</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t('api_key_label')}</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type={showOpenai ? 'text' : 'password'}
                      value={openaiKey}
                      onChange={(e) => setOpenaiKey(e.target.value)}
                      placeholder="sk-..."
                      data-testid="settings-ai-openai-key-input"
                    />
                    <button type="button" onClick={() => setShowOpenai(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]">
                      {showOpenai ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => testKey('openai')} disabled={!openaiKey || testing.openai} data-testid="settings-ai-openai-test-button">
                    <TestTube2 className="h-3.5 w-3.5 mr-1" /> {testing.openai ? '...' : t('ai_test')}
                  </Button>
                  <Button size="sm" onClick={async () => { await saveSection({ openai_api_key: openaiKey }); setOpenaiKey(''); }} disabled={!openaiKey} data-testid="settings-ai-openai-save-button">
                    <Save className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>

            {/* ANTHROPIC */}
            <div className="border rounded-xl p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-semibold text-sm">Anthropic (Claude)</h3>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    {s.anthropic_api_key_set ? `✓ Configurado (${s.anthropic_api_key_masked})` : (lang === 'es' ? 'No configurado' : 'Não configurado')}
                  </p>
                </div>
              </div>
              <div><Label className="text-xs">{t('ai_default_model')}</Label>
                <Select value={s.anthropic_model} onValueChange={(v) => saveSection({ anthropic_model: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="claude-sonnet-4-5">claude-sonnet-4-5</SelectItem>
                    <SelectItem value="claude-3-5-sonnet-20241022">claude-3-5-sonnet</SelectItem>
                    <SelectItem value="claude-3-5-haiku-20241022">claude-3-5-haiku</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t('api_key_label')}</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type={showAnthropic ? 'text' : 'password'}
                      value={anthropicKey}
                      onChange={(e) => setAnthropicKey(e.target.value)}
                      placeholder="sk-ant-..."
                      data-testid="settings-ai-anthropic-key-input"
                    />
                    <button type="button" onClick={() => setShowAnthropic(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]">
                      {showAnthropic ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => testKey('anthropic')} disabled={!anthropicKey || testing.anthropic} data-testid="settings-ai-anthropic-test-button">
                    <TestTube2 className="h-3.5 w-3.5 mr-1" /> {testing.anthropic ? '...' : t('ai_test')}
                  </Button>
                  <Button size="sm" onClick={async () => { await saveSection({ anthropic_api_key: anthropicKey }); setAnthropicKey(''); }} disabled={!anthropicKey} data-testid="settings-ai-anthropic-save-button">
                    <Save className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>

            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              {lang === 'es'
                ? 'Las claves se guardan cifradas en tu base de datos. Puedes cambiarlas en cualquier momento sin reiniciar.'
                : 'As chaves são guardadas no banco. Pode trocar a qualquer momento sem reiniciar.'}
            </p>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ColorField({ label, value, onChange, testId }) {
  const hex = hslStringToHex(value || '');
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-2 items-center">
        <input
          type="color"
          value={hex}
          onChange={(e) => onChange(hexToHslString(e.target.value))}
          className="h-9 w-12 rounded-md border cursor-pointer"
          data-testid={testId}
        />
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="32 78% 52%"
          className="font-mono text-xs"
        />
      </div>
    </div>
  );
}
